import { create } from "zustand";

/**
 * Live consent state for the current page.
 *
 * Deliberately NOT persisted: the cookie is the source of truth, because it is
 * the only store every `cedehub.io` host can read. This is just the in-memory
 * projection the React tree renders from.
 *
 * `unknown` is the initial value on every render, server and client alike, so
 * the prerendered HTML is identical for every visitor.
 */
export type ConsentStatus = "unknown" | "asking" | "decided";

interface ConsentState {
  status: ConsentStatus;
  /** Whether analytics and advertising tags may load. False until decided. */
  analytics: boolean;
  setDecided: (analytics: boolean) => void;
  setAsking: () => void;
}

export const useConsentStore = create<ConsentState>((set) => ({
  status: "unknown",
  analytics: false,
  setDecided: (analytics) => set({ status: "decided", analytics }),
  setAsking: () => set({ status: "asking" }),
}));

/** Whether non-essential tags may load right now. */
/** Whether non-essential tags and analytics sends are allowed right now. */
export const analyticsAllowed = (): boolean => {
  const { status, analytics } = useConsentStore.getState();
  return status === "decided" && analytics;
};

export const useAnalyticsAllowed = (): boolean =>
  useConsentStore((state) => state.status === "decided" && state.analytics);
